import PropTypes from "prop-types";
import { useEffect, useRef } from "react";
import { useSelector } from "react-redux";

// Default Sections
import { HolidayPayDefault } from "@/PayPlans/helpers/DefaultSections";

// Helpers
import { clone } from "@/Common/helpers/Modifiers/formatters";
import { resolveDateTime, getHolidays } from "@/Common/helpers/Dates";

// Hooks
import { useCallbackState } from "@/Common/hooks/CallbackState";

// Layouts components
import LineInputsLoader from "@/Layouts/components/Skeletons/LineInputsLoader";
import SmartSelector from "@/Layouts/components/InputComponents/SmartSelector";
import TierAlertError from "@/Layouts/components/Tiers/TierAlertError";

// MUI
import { Box, Grid, Stack } from "@mui/material";

const orderHolidaysByDate = (holidays = []) => holidays.toSorted((a, b) => resolveDateTime(a.date) - resolveDateTime(b.date));

const dropdownStyles = { width: "220px" };

const sortObjectKeys = (obj) => {
	return Object.keys(obj)
		.sort((a, b) => a.localeCompare(b))
		.reduce((result, key) => {
			result[key] = obj[key];
			return result;
		}, {});
};

const compareArraysIgnoringOrder = (arr1, arr2) => {
	if (arr1.length !== arr2.length) {
		return false;
	}

	// Sort both arrays by JSON string representation of their elements
	const sortedArr1 = [...arr1].map(sortObjectKeys).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));
	const sortedArr2 = [...arr2].map(sortObjectKeys).sort((a, b) => JSON.stringify(a).localeCompare(JSON.stringify(b)));

	// Use JSON.stringify to compare the sorted arrays
	return JSON.stringify(sortedArr1) === JSON.stringify(sortedArr2);
};

export default function HolidayPay(props) {
	const {
		sectionId = "holidayPay",
		content = {
			...HolidayPayDefault(),
		},
		loading = false,
		readOnly = false,
		useSectionsErrors = () => {},
		onActionUpdate = () => {},
	} = props;

	// Global State
	const location = useSelector((state) => state.location.location);
	const countryCode = location?.geoDetails?.countryCode || "US";
	const holidays = getHolidays(new Date().getFullYear(), countryCode);

	// Used to add errors to the pay plan context
	const { addSectionError, removeSectionError, clearSectionErrors } = useSectionsErrors?.() || {};

	// Control to send update actions
	const wasUpdated = useRef(false);
	const onHolidayPayUpdate = (updatedHolidayPay) => {
		const isValid = holidayPay?.holidaysSelected?.length;
		isValid ? removeSectionError?.(sectionId) : addSectionError?.(sectionId);
		if (isValid && wasUpdated.current) {
			onActionUpdate?.(sectionId, null, updatedHolidayPay);
		}
	};

	// States
	const [holidayPay, setHolidayPay] = useCallbackState(null, onHolidayPayUpdate);
	const selectedHolidaysError = !holidayPay?.holidaysSelected?.length;

	const handleSelectDays = (selected = []) => {
		const newSelected = selected.map((item) => ({
			date: item.date,
			localHolidayName: item.localHolidayName,
			holidayName: item.holidayName,
		}));
		const updated = { ...holidayPay };
		const isDataChanged = !compareArraysIgnoringOrder(newSelected, holidayPay?.holidaysSelected);
		if (isDataChanged) {
			updated["holidaysSelected"] = orderHolidaysByDate(newSelected);
			update(updated);
		}
	};

	const handleUpdateHolidayType = (selected) => {
		const updated = { ...holidayPay };
		updated["holidayByType"] = selected?.value;
		update(updated);
	};

	const update = (updatedHolidayPay) => {
		setHolidayPay(updatedHolidayPay);
		wasUpdated.current = true;
	};

	useEffect(() => {
		setHolidayPay(clone(content));
		return () => {
			setHolidayPay(null);
			wasUpdated.current = false;
			clearSectionErrors?.(sectionId);
		};
	}, [sectionId, content, setHolidayPay, clearSectionErrors]);

	const byPTOModalityString = "By PTO Modality";

	return (
		<Grid container direction="column" mt={2} sx={{ padding: "0 32px" }}>
			{loading ? (
				<Stack>
					<LineInputsLoader title inputsAmount={2} justifyContent={"flex-start"} mt={0} />
				</Stack>
			) : (
				<Stack direction={"column"} spacing={2}>
					<Stack direction={"row"} justifyContent={"space-between"} alignItems={"center"} width={1}>
						<Box width={"220px"}>
							<SmartSelector
								multiple
								multipleSelectAll
								tagsVariant={"text"}
								selectBy={"holidayName"}
								showBy={"localHolidayName"}
								options={holidays || []}
								selected={holidayPay?.holidaysSelected || []}
								inputProps={{
									placeholder: "Select Holiday(s)",
								}}
								listBoxProps={{
									sx: { maxHeight: "220px" },
								}}
								clearable
								disabled={readOnly || loading}
								error={selectedHolidaysError}
								loading={loading}
								sx={dropdownStyles}
								onChangeAction={handleSelectDays}
							/>
						</Box>
						<Box width={"13px"}></Box>
						<SmartSelector
							selectBy={"value"}
							showBy={"label"}
							options={[
								{ label: byPTOModalityString, value: byPTOModalityString },
								{ label: "By Pay Rate", value: "By Pay Rate" },
							]}
							selected={holidayPay?.holidayByType || byPTOModalityString}
							disabled={readOnly || loading}
							loading={loading}
							sx={{ maxWidth: "180px" }}
							onChangeAction={(selected) => handleUpdateHolidayType(selected)}
						/>
					</Stack>

					{selectedHolidaysError && (
						<Stack mt={1}>
							<TierAlertError key={"holiday-pay-error"} text="Select at least one holiday" />
						</Stack>
					)}
				</Stack>
			)}
		</Grid>
	);
}
HolidayPay.propTypes = {
	sectionId: PropTypes.string,
	content: PropTypes.object,
	useSectionsErrors: PropTypes.func,
	loading: PropTypes.bool,
	readOnly: PropTypes.bool,
	onActionUpdate: PropTypes.func,
};
